import { useCallback, useSyncExternalStore } from 'react';
import type { ReactElement } from 'react';
import { appThemeApiRef, useApi } from '@backstage/frontend-plugin-api';
import type { AppThemeApi } from '@backstage/frontend-plugin-api';
import {
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  TooltipTrigger,
} from '@backstage/ui';
import { makeStyles } from '@material-ui/core';
import { RiComputerLine, RiMoonLine, RiSunLine } from '@remixicon/react';
import {
  THEME_MODES,
  isThemeMode,
  themeIdOf,
  themeModeLabels,
  themeModeOf,
  type ThemeMode,
} from './themeMode';

/**
 * Sun, moon, and a screen for Auto: the system's own setting. Decorative:
 * every use names the mode in words (accessible name, menu item text).
 */
export const themeModeIcons: Record<ThemeMode, ReactElement> = {
  light: <RiSunLine aria-hidden focusable="false" />,
  dark: <RiMoonLine aria-hidden focusable="false" />,
  auto: <RiComputerLine aria-hidden focusable="false" />,
};

// The rules below are not in a cascade layer, so they win over BUI's own,
// which all sit in `@layer components` — whatever their specificity, which is
// how they undo the group's outline and dividers and the solid accent BUI
// gives a selected or pressed ToggleButton.
const useStyles = makeStyles({
  // A neutral-2 track on the sidebar's neutral-1 (navigation.background,
  // themes.ts), as wide as its container: in the user block, the active nav
  // item's pill (SidebarUserBlock).
  track: {
    display: 'flex',
    width: '100%',
    gap: 3,
    padding: 3,
    borderRadius: 9,
    overflow: 'visible',
    boxShadow: 'none',
    background: 'var(--bui-bg-neutral-2)',
  },
  segment: {
    flex: '1 1 0',
    height: 26,
    padding: 0,
    border: 0,
    borderRadius: 7,
    background: 'transparent',
    color: 'var(--bui-fg-secondary)',
    boxShadow: 'none',
    '& svg': { width: 17, height: 17 },
    '&:not([data-selected])[data-hovered], &:not([data-selected])[data-pressed]':
      {
        background: 'var(--bui-bg-neutral-2-hover)',
        color: 'var(--bui-fg-primary)',
      },
    // The active mode is raised: the sidebar's own surface, primary ink.
    '&[data-selected]': {
      background: 'var(--bui-bg-neutral-1)',
      color: 'var(--bui-fg-primary)',
      boxShadow: 'var(--waadoo-shadow-card)',
    },
    // BUI's focus ring (an inset box-shadow), which the raised segment's
    // shadow above would otherwise replace.
    '&[data-focus-visible]': {
      boxShadow: 'inset 0 0 0 2px var(--bui-ring)',
    },
  },
});

/**
 * The active theme id, kept current: the same pair of calls the Settings
 * page's toggle (plugin-user-settings' UserSettingsThemeToggle) observes.
 */
function useActiveThemeId(appThemeApi: AppThemeApi): string | undefined {
  const subscribe = useCallback(
    (onChange: () => void) => {
      const subscription = appThemeApi.activeThemeId$().subscribe(onChange);
      return () => subscription.unsubscribe();
    },
    [appThemeApi],
  );
  return useSyncExternalStore(subscribe, () => appThemeApi.getActiveThemeId());
}

/**
 * The current mode, and the way to change it. Auto clears the stored theme,
 * and the app then follows the system's `prefers-color-scheme` — the Settings
 * page's semantics, through the same API. Shared by the segmented control
 * below and the closed sidebar's menu (SidebarUserBlock).
 */
export function useThemeMode(): {
  mode: ThemeMode;
  select: (next: ThemeMode) => void;
} {
  const appThemeApi = useApi(appThemeApiRef);
  const mode = themeModeOf(useActiveThemeId(appThemeApi));
  const select = (next: ThemeMode) => {
    appThemeApi.setActiveThemeId(themeIdOf(next));
  };
  return { mode, select };
}

/**
 * The Settings page's « Thème » choice, under the user's name in the open
 * sidebar (SidebarUserBlock): Clair, Sombre or Auto, a three-segment control
 * (radio group) of icons, each named and with its tooltip.
 */
export const SidebarThemeSwitch = () => {
  const classes = useStyles();
  const { mode, select } = useThemeMode();

  return (
    <ToggleButtonGroup
      aria-label="Thème"
      selectionMode="single"
      // Clicking the selected segment again keeps it: there is always a mode.
      disallowEmptySelection
      selectedKeys={[mode]}
      onSelectionChange={keys => {
        const [selected] = keys;
        if (isThemeMode(selected)) {
          select(selected);
        }
      }}
      className={classes.track}
    >
      {THEME_MODES.map(option => (
        <TooltipTrigger key={option}>
          <ToggleButton
            id={option}
            aria-label={themeModeLabels[option]}
            className={classes.segment}
          >
            {themeModeIcons[option]}
          </ToggleButton>
          <Tooltip placement="bottom">{themeModeLabels[option]}</Tooltip>
        </TooltipTrigger>
      ))}
    </ToggleButtonGroup>
  );
};
